import React, { createContext, useContext, useState, useEffect } from 'react';
import { authStorage } from '@/lib/authStorage';

interface AuthContextType {
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (accessToken: string, refreshToken: string, username: string) => void;
    logout: () => void;
    getAccessToken: () => string | null;
    getUsername: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Initialize authentication state from localStorage immediately
const getInitialAuthState = (): boolean => {
    if (typeof window === 'undefined') return false;
    const token = authStorage.getAccessToken();
    return !!token;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(getInitialAuthState);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        // Verify token on mount
        const token = authStorage.getAccessToken();
        setIsAuthenticated(!!token);
        setIsLoading(false);
    }, []);

    const login = (accessToken: string, refreshToken: string, username: string) => {
        authStorage.setTokens(accessToken, refreshToken);
        authStorage.setUsername(username);
        setIsAuthenticated(true);
    };

    const logout = () => {
        authStorage.clear();
        setIsAuthenticated(false);
    };

    const getAccessToken = () => {
        return authStorage.getAccessToken();
    };

    const getUsername = () => {
        return authStorage.getUsername();
    };

    return (
        <AuthContext.Provider value={{ isAuthenticated, isLoading, login, logout, getAccessToken, getUsername }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
